import LegalPage from "./components/LegalPage";

const content = `
IClimbTrees ("we," "us," or "the platform," found at https://iclimbtrees.com) is a free, not-for-profit educational platform that helps adults learn advanced mathematics. IClimbTrees is currently operated as an independent, unincorporated project based in Canada. This Privacy Policy explains what information we collect, how we use it, and the choices you have.

By using IClimbTrees, you agree to the practices described in this policy. If you do not agree, please do not use the platform.

## 1. Who This Policy Applies To

IClimbTrees is intended for adult learners. It is not directed at children, and we do not knowingly collect personal information from anyone under the age of 18. If we learn that we have collected information from a child under 18, we will delete it.

## 2. Information We Collect

### a) Information you provide directly

- **Account information**: When you sign in with Google, we receive your name and email address from Google to create and manage your account.
- **Notes, highlights, and bookmarks**: Content you create while studying on the platform.
- **Feedback on your understanding**: Self-reported responses about how well you understood a section of material.
- **Questions you ask our AI tutor**: The text of questions you submit to the AI assistance feature, and the responses given.
- **Newsletter sign-up**: If you opt in, your email address, so we can send you updates.

### b) Information generated by your use of the platform

- **Course and quiz activity**: Which courses you've started or completed, quiz answers, scores, and progress through material.

### c) Information we do not collect

We do not collect payment information — IClimbTrees is entirely free to use. We do not run advertising or ad-tracking on the site.

## 3. How We Use Your Information

We use the information above to:

- Provide your account, save your progress, and let you pick up where you left off
- Show you your notes, highlights, and bookmarks
- Power the AI tutor feature, by sending your question (and relevant context) to our AI provider to generate a response
- Send password-reset emails if you request one
- Send newsletter updates, only if you've opted in
- Understand, in aggregate, how learners are progressing through material, so we can improve the courses

We do not sell your personal information, and we do not use your notes, quiz answers, or AI questions to build advertising profiles.

## 4. Third Parties We Share Information With

We use a small number of third-party services to run IClimbTrees. Each only receives the information it needs to perform its function:

| Service | Purpose | What's shared |
|---|---|---|
| **Google** | Sign-in / authentication | Your Google account info (name, email) via OAuth |
| **Brevo** | Mailing list and password-reset emails | Your email address, and newsletter opt-in status |
| **OpenAI (ChatGPT)** | Powers the AI tutor feature | The text of questions you ask the AI, and any course context needed to answer them |

These providers process data on our behalf under their own privacy and security terms. We do not otherwise sell, rent, or share your personal information with third parties for their own marketing purposes.

## 5. Data Retention

We keep your account information, notes, progress, and AI conversation history for as long as your account is active, so that your learning history is preserved. If you delete your account (see Section 7), we will delete this information within a reasonable period, except where we're required to keep something for legal reasons.

## 6. Data Security

We take reasonable steps to protect your information, including relying on the security practices of our service providers (Google, Brevo, OpenAI). However, no online service can guarantee perfect security, and we can't promise that your information will never be accessed by an unauthorized party.

## 7. Your Choices and Rights

You can:

- **Access or export** your notes, bookmarks, and progress by contacting us
- **Unsubscribe** from the newsletter at any time via the unsubscribe link in any email, or by contacting us
- **Delete your account**, which will remove your personal data from our active systems, by emailing us
- **Ask questions** about what data we hold on you

If you are located in the European Union or United Kingdom, you also have rights under the GDPR, including the right to access, correct, delete, or restrict the use of your personal data, and the right to lodge a complaint with a data protection authority. If you are located in Canada, our handling of your personal information is guided by the principles of the Personal Information Protection and Electronic Documents Act (PIPEDA).

To exercise any of these rights, contact us at the email below.

## 8. Cookies

IClimbTrees may use essential cookies or similar local storage to keep you signed in and remember your preferences. We do not currently use third-party advertising cookies.

## 9. Changes to This Policy

We may update this policy as the platform grows. If we make material changes, we'll update the "Last updated" date above, and where appropriate, notify users via email or a notice on the site.

## 10. Contact Us

If you have questions about this Privacy Policy or how your data is handled, contact us at:

**Email:** iclimbtreesmail@gmail.com

---

*This policy is provided as a good-faith description of our practices and is not a substitute for legal advice.*
`;

export default function PrivacyPage() {
  return (
    <LegalPage
      title="Privacy Policy for IClimbTrees"
      lastUpdated="July 6, 2026"
      content={content}
    />
  );
}
